/*
 * Food label ("címke") layout + printer command builder — pure, dependency-free.
 *
 * One label = one food box:
 *   1. print date/time        `YYYY-MM-DD HH:mm`
 *   2. food name              bigger + bold, wrapped (max 2–3 lines)
 *   3. label text             shelf life / allergen note (profile `labelText`)
 *
 * The layout auto-fits the configured label stock (any width × height in mm)
 * and is rendered to the printer's own command language, so it works over raw
 * TCP (port 9100) and raw USB without a driver:
 *   - `zpl`    Zebra ZPL II — also Bixolon XD (BPL-Z emulation), Honeywell, …
 *   - `tspl`   TSC TSPL/TSPL2 — TSC, Xprinter and most generic label printers
 *   - `escpos` receipt-style printers: the apps render it with their own ESC/POS
 *              stack; `planEscposLabel` supplies the wrapped lines.
 *
 * Kept in sync with Happy-POS `src/printing/labelCommands.js` (same layout on
 * the Windows POS and on Android).
 */

const DEFAULT_LABEL_TEXT = 'Eltarthatóság: 48 óra 0-8 °C, fagyasztva 6 hónap\nAllergének: lásd www.maharaja.hu oldalunkon';
const DEFAULT_LABEL_SIZE = {widthMm: 57, heightMm: 40};
const DEFAULT_LABEL_DPI = 203;
const DEFAULT_LABEL_GAP_MM = 2;
const LABEL_LANGUAGES = ['zpl', 'tspl', 'escpos'];
const MIN_LABEL_MM = 10;
const MAX_LABEL_MM = 300;
/** Text direction: 0 = across the label width, 90 = along the label length (rotated clockwise). */
const LABEL_ROTATIONS = [0, 90];
/** Print width of a 4-inch head (Bixolon XD5-40, Zebra ZD4xx) — a wider label is cut off. */
const MAX_PRINT_HEAD_MM = 104;

/** Common label stock sizes (width × height, mm) offered by the settings screens. */
const LABEL_SIZE_PRESETS = [
  {id: '40x30', widthMm: 40, heightMm: 30},
  {id: '40x100', widthMm: 40, heightMm: 100},
  {id: '50x30', widthMm: 50, heightMm: 30},
  {id: '57x40', widthMm: 57, heightMm: 40},
  {id: '58x40', widthMm: 58, heightMm: 40},
  {id: '60x40', widthMm: 60, heightMm: 40},
  {id: '100x50', widthMm: 100, heightMm: 50},
  {id: '100x150', widthMm: 100, heightMm: 150},
  {id: '200x50', widthMm: 200, heightMm: 50},
];

// TSPL built-in bitmap fonts (monospace, 203 dpi dot sizes). Supported by
// every TSPL firmware, unlike the scalable font "0".
const TSPL_BITMAP_FONTS = [
  {id: '1', w: 8, h: 12},
  {id: '2', w: 12, h: 20},
  {id: '3', w: 16, h: 24},
  {id: '4', w: 24, h: 32},
  {id: '5', w: 32, h: 48},
];

// Scalable ZPL font 0 is condensed; 0.6 × height over-estimates the average
// glyph advance, so our pre-wrapped lines never overflow the field block.
const ZPL_CHAR_WIDTH_RATIO = 0.6;

const pad2 = (value) => String(value).padStart(2, '0');

const toPositiveNumber = (value) => {
  const number = Number(value);

  return Number.isFinite(number) && number > 0 ? number : 0;
};

const clampLabelMm = (value) => Math.max(MIN_LABEL_MM, Math.min(MAX_LABEL_MM, Math.round(value)));

const firstNonEmptyString = (...values) => {
  const found = values.find((value) => typeof value === 'string' && value.trim());

  return found ? found.trim() : '';
};

/**
 * Formats the label timestamp as `YYYY-MM-DD HH:mm` in local time.
 *
 * @param {Date|string|number} [date] defaults to now
 * @returns {string}
 */
const formatLabelDateTime = (date = new Date()) => {
  const parsed = date instanceof Date ? date : new Date(date);
  const value = Number.isNaN(parsed.getTime()) ? new Date() : parsed;

  return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())} ` +
    `${pad2(value.getHours())}:${pad2(value.getMinutes())}`;
};

/**
 * Name printed on the label: the order line's `remoteName`, falling back to the
 * food's localized name. Never returns an object.
 *
 * @param {object} cartItem an order/cart line
 * @returns {string}
 */
const resolveLabelName = (cartItem = {}) => {
  const item = cartItem || {};
  const food = item.item && typeof item.item === 'object' ? item.item : {};
  const localized = (name) => (name && typeof name === 'object' ? firstNonEmptyString(name.hu, name.en) : '');

  return firstNonEmptyString(
    item.remoteName,
    localized(food.name),
    localized(item.name),
    typeof item.name === 'string' ? item.name : '',
    typeof food.name === 'string' ? food.name : ''
  );
};

/**
 * Number of labels for an order line: one per ordered piece, so every box gets
 * a sticker. Fractional amounts (weighed items) are one package → one label.
 *
 * @param {object} cartItem an order/cart line
 * @returns {number} positive integer
 */
const resolveLabelCopies = (cartItem = {}) => {
  const quantity = Number(cartItem && (cartItem.amount || cartItem.quantity));

  return Number.isInteger(quantity) && quantity > 0 ? Math.min(quantity, 999) : 1;
};

/**
 * @param {string} value any language id
 * @returns {'zpl'|'tspl'|'escpos'} a supported language (default `zpl`)
 */
const normalizeLabelLanguage = (value) => {
  const language = String(value || '').trim().toLowerCase();

  return LABEL_LANGUAGES.includes(language) ? language : 'zpl';
};

/**
 * @param {*} value
 * @returns {0|90}
 */
const normalizeLabelRotation = (value) => (Number(value) === 90 ? 90 : 0);

/**
 * Resolves the label settings of a `label` printer profile. Device-local printer
 * options (POS printer settings) win over the profile stored on the server,
 * which wins over the defaults. A device size is only used when it has a height
 * (a label needs one); sizes are clamped to 10–300 mm.
 *
 * @param {object} [event] printer profile (`labelSize{widthMM,heightMM}`, `labelRotation`, `labelLanguage`, `labelDpi`, `labelGapMm`, `labelText`)
 * @param {object} [options] device options (`paperWidthMm`, `paperHeightMm`, `labelRotation`, `labelLanguage`, `labelDpi`, `labelGapMm`, `labelText`)
 * @returns {{widthMm: number, heightMm: number, dpi: number, gapMm: number, language: string, text: string, rotation: 0|90}}
 */
const resolveLabelSettings = (event = {}, options = {}) => {
  const profile = event || {};
  const device = options || {};
  const stock = profile.labelSize || {};
  const deviceHeight = toPositiveNumber(device.paperHeightMm);
  const deviceWidth = toPositiveNumber(device.paperWidthMm);

  const widthMm = (deviceHeight && deviceWidth) || toPositiveNumber(stock.widthMM) || DEFAULT_LABEL_SIZE.widthMm;
  const heightMm = deviceHeight || toPositiveNumber(stock.heightMM) || DEFAULT_LABEL_SIZE.heightMm;
  const dpi = toPositiveNumber(device.labelDpi) || toPositiveNumber(profile.labelDpi) || DEFAULT_LABEL_DPI;
  const gapRaw = [device.labelGapMm, profile.labelGapMm].find((v) => v !== undefined && v !== null && v !== '' && Number.isFinite(Number(v)));
  const rotationRaw = [device.labelRotation, profile.labelRotation].find((v) => v !== undefined && v !== null && v !== '');

  return {
    widthMm: clampLabelMm(widthMm),
    heightMm: clampLabelMm(heightMm),
    dpi: Math.max(100, Math.min(600, Math.round(dpi))),
    gapMm: gapRaw === undefined ? DEFAULT_LABEL_GAP_MM : Math.max(0, Math.min(20, Number(gapRaw))),
    language: normalizeLabelLanguage(device.labelLanguage || profile.labelLanguage),
    text: firstNonEmptyString(device.labelText, profile.labelText) || DEFAULT_LABEL_TEXT,
    rotation: normalizeLabelRotation(rotationRaw),
  };
};

const LATIN1_REPLACEMENTS = {
  'ő': 'ö', 'ű': 'ü', 'Ő': 'Ö', 'Ű': 'Ü',
  '–': '-', '—': '-', '„': '"', '“': '"', '”': '"', '‘': "'", '’': "'", '…': '...', '€': 'EUR',
};

/**
 * Maps text onto Latin-1 — the glyph set of the printers' built-in fonts. The
 * Hungarian double-acute letters become their umlaut pair (ő→ö, ű→ü), other
 * accents fall back to the bare letter, anything else to `?`.
 *
 * @param {string} value
 * @returns {string}
 */
const toLatin1Text = (value = '') => Array.from(String(value)).map((char) => {
  if (LATIN1_REPLACEMENTS[char]) return LATIN1_REPLACEMENTS[char];
  if (char.charCodeAt(0) <= 0xff) return char;
  const stripped = char.normalize('NFD').replace(/[̀-ͯ]/g, '');

  return stripped && stripped.charCodeAt(0) <= 0xff ? stripped : '?';
}).join('');

/**
 * Greedy word wrap. Honours `\n`, hard-splits words longer than a line.
 *
 * @param {string} text
 * @param {number} maxChars characters per line (>= 1)
 * @returns {string[]}
 */
const wrapLabelText = (text = '', maxChars = 32) => {
  const limit = Math.max(1, Math.floor(maxChars));
  const lines = [];

  String(text).replace(/\r/g, '').split('\n').forEach((paragraph) => {
    let line = '';
    paragraph.split(/\s+/).filter(Boolean).forEach((word) => {
      let rest = word;
      while (rest.length > limit) {
        if (line) {
          lines.push(line);
          line = '';
        }
        lines.push(rest.slice(0, limit));
        rest = rest.slice(limit);
      }
      if (!rest) return;
      if (!line) {
        line = rest;
      } else if (line.length + 1 + rest.length <= limit) {
        line = `${line} ${rest}`;
      } else {
        lines.push(line);
        line = rest;
      }
    });
    if (line) lines.push(line);
  });

  return lines;
};

const truncateLines = (lines, maxLines) => {
  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const last = kept[maxLines - 1];
  kept[maxLines - 1] = last.length > 2 ? `${last.slice(0, last.length - 2)}..` : last;

  return kept;
};

/**
 * Lays the three label blocks out on the label (in printer dots). `measure`
 * quantizes a wanted font height to what the language can print and reports
 * the per-character advance, so wrapping matches the real font.
 *
 * @param {object} input
 * @param {number} input.widthMm
 * @param {number} input.heightMm
 * @param {number} [input.dpi]
 * @param {string} input.date formatted timestamp
 * @param {string} input.name food name
 * @param {string} [input.text] label text
 * @param {function(number): {height: number, charWidth: number}} input.measure
 * @returns {{width: number, height: number, margin: number, innerWidth: number,
 *   blocks: Array<{kind: string, font: object, lines: Array<{text: string, y: number}>}>}}
 */
const planLabelLayout = ({widthMm, heightMm, dpi = DEFAULT_LABEL_DPI, date, name, text = '', measure}) => {
  const dotsPerMm = dpi / 25.4;
  const width = Math.round(widthMm * dotsPerMm);
  const height = Math.round(heightMm * dotsPerMm);
  const margin = Math.max(8, Math.round((heightMm < 25 ? 1.2 : 2) * dotsPerMm));
  const innerWidth = Math.max(1, width - margin * 2);
  const innerHeight = Math.max(1, height - margin * 2);
  const blockGap = Math.round(0.8 * dotsPerMm);
  const mm = (value) => value * dotsPerMm;
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const charsPerLine = (font) => Math.max(1, Math.floor(innerWidth / font.charWidth));
  const maxNameLines = heightMm >= 45 ? 3 : 2;

  let dateFont = measure(clamp(innerHeight * 0.13, mm(2.2), mm(5)));
  let nameFont = measure(clamp(innerHeight * 0.24, mm(3), mm(12)));
  let textFont = measure(clamp(innerHeight * 0.09, mm(2), mm(3.5)));
  const minName = mm(2.6);
  const minText = mm(1.6);

  const wrapName = () => wrapLabelText(name, charsPerLine(nameFont));
  const wrapBody = () => wrapLabelText(text, charsPerLine(textFont));
  const lineHeight = (font) => Math.round(font.height * 1.12);
  const usedHeight = (nameLines, textLines) => dateFont.height + blockGap +
    nameLines.length * lineHeight(nameFont) +
    (textLines.length ? blockGap + textLines.length * lineHeight(textFont) : 0);

  // Name: shrink until it fits the allowed line count.
  let nameLines = wrapName();
  for (let guard = 0; nameLines.length > maxNameLines && nameFont.height > minName && guard < 20; guard += 1) {
    const next = measure(nameFont.height * 0.88);
    if (next.height >= nameFont.height) break;
    nameFont = next;
    nameLines = wrapName();
  }
  nameLines = truncateLines(nameLines, maxNameLines);

  // Text first, then the name, shrink until everything fits vertically.
  let textLines = wrapBody();
  for (let guard = 0; usedHeight(nameLines, textLines) > innerHeight && guard < 40; guard += 1) {
    const shrinkText = textLines.length && textFont.height > minText;
    const shrinkName = !shrinkText && nameFont.height > minName;
    if (!shrinkText && !shrinkName) break;
    const target = shrinkText ? textFont : nameFont;
    const next = measure(target.height * 0.9);
    if (next.height >= target.height) break;
    if (shrinkText) textFont = next; else nameFont = next;
    nameLines = truncateLines(wrapName(), maxNameLines);
    textLines = wrapBody();
  }
  // Still too tall at minimum sizes → drop the text lines that do not fit.
  while (textLines.length && usedHeight(nameLines, textLines) > innerHeight) {
    textLines = textLines.slice(0, -1);
  }

  // Centre the stack vertically on the label.
  let y = margin + Math.max(0, Math.floor((innerHeight - usedHeight(nameLines, textLines)) / 2));
  const place = (lines, font) => lines.map((line) => {
    const placed = {text: line, y};
    y += lineHeight(font);

    return placed;
  });
  const blocks = [];
  blocks.push({kind: 'date', font: dateFont, lines: place([date], dateFont)});
  y += blockGap - (lineHeight(dateFont) - dateFont.height);
  blocks.push({kind: 'name', font: nameFont, lines: place(nameLines, nameFont)});
  if (textLines.length) {
    y += blockGap;
    blocks.push({kind: 'text', font: textFont, lines: place(textLines, textFont)});
  }

  return {width, height, margin, innerWidth, blocks};
};

const measureZplFont = (wanted) => {
  const height = Math.max(10, Math.round(wanted));

  return {height, charWidth: height * ZPL_CHAR_WIDTH_RATIO};
};

const measureTsplFont = (wanted) => {
  let best = null;
  TSPL_BITMAP_FONTS.forEach((font) => {
    for (let mul = 1; mul <= 8; mul += 1) {
      const height = font.h * mul;
      if (height <= wanted && (!best || height > best.height)) {
        best = {id: font.id, mul, height, charWidth: font.w * mul};
      }
    }
  });

  return best || {id: '1', mul: 1, height: 12, charWidth: 8};
};

/**
 * Plans the layout in the text's own frame: with `rotation: 90` the text runs
 * along the label length, so width and height swap for the planner.
 *
 * @param {object} input builder input
 * @param {function(number): object} measure
 * @returns {{plan: object, rotated: boolean}}
 */
const planOrientedLayout = (input, measure) => {
  const content = labelContent(input);
  const rotated = normalizeLabelRotation(input.rotation) === 90;
  const widthMm = rotated ? input.heightMm : input.widthMm;
  const heightMm = rotated ? input.widthMm : input.heightMm;
  const plan = planLabelLayout({...input, ...content, widthMm, heightMm, dpi: input.dpi || DEFAULT_LABEL_DPI, measure});

  return {plan, rotated};
};

const escapeZpl = (value) => String(value).replace(/[\^~]/g, ' ').replace(/\\/g, '/');
const escapeTspl = (value) => String(value).replace(/"/g, '\\["]');

const labelContent = (input = {}) => ({
  date: toLatin1Text(input.date || formatLabelDateTime()),
  name: toLatin1Text(input.name || ''),
  text: toLatin1Text(input.text === undefined ? DEFAULT_LABEL_TEXT : input.text),
});

/**
 * ZPL II label (UTF-8 via `^CI28`, scalable font 0). Copies via `^PQ`.
 *
 * With `rotation: 90` the fields are drawn `R` (clockwise): the first line
 * sits at the right edge and the text reads top to bottom along the length.
 *
 * @param {object} input {widthMm, heightMm, dpi, rotation, date, name, text, copies}
 * @returns {string} ZPL program — send UTF-8 encoded
 */
const buildZplLabel = (input = {}) => {
  const {plan, rotated} = planOrientedLayout(input, measureZplFont);
  // ^PW is always the width across the print head, ^LL the length along the feed.
  const printWidth = rotated ? plan.height : plan.width;
  const printLength = rotated ? plan.width : plan.height;
  const out = ['^XA', '^CI28', `^PW${printWidth}`, `^LL${printLength}`, '^LH0,0'];

  plan.blocks.forEach((block) => {
    const size = block.font.height;
    block.lines.forEach((line) => {
      const field = `^A0${rotated ? 'R' : 'N'},${size},${size}^FB${plan.innerWidth},1,0,C,0^FD${escapeZpl(line.text)}^FS`;
      // ^FO is the physical upper-left corner of the field, whatever its rotation.
      const origin = (shift) => (rotated
        ? `^FO${printWidth - line.y - size},${plan.margin + shift}`
        : `^FO${plan.margin + shift},${line.y}`);
      out.push(`${origin(0)}${field}`);
      // Bold name: second pass shifted by ~0.25 mm.
      if (block.kind === 'name') out.push(`${origin(2)}${field}`);
    });
  });
  out.push(`^PQ${Math.max(1, Math.round(input.copies || 1))},0,1,Y`, '^XZ');

  return out.join('\n') + '\n';
};

/**
 * TSPL label (bitmap fonts, CODEPAGE 1252). Copies via `PRINT 1,n`.
 *
 * With `rotation: 90` TEXT is rotated clockwise around its reference point
 * (the glyphs' top-left corner), which therefore sits at the line's right edge.
 *
 * @param {object} input {widthMm, heightMm, dpi, gapMm, rotation, date, name, text, copies}
 * @returns {string} TSPL program — send Latin-1 encoded
 */
const buildTsplLabel = (input = {}) => {
  const {plan, rotated} = planOrientedLayout(input, measureTsplFont);
  const gapMm = input.gapMm === undefined ? DEFAULT_LABEL_GAP_MM : input.gapMm;
  const out = [
    `SIZE ${input.widthMm} mm,${input.heightMm} mm`,
    `GAP ${gapMm} mm,0 mm`,
    'DIRECTION 1',
    'REFERENCE 0,0',
    'CODEPAGE 1252',
    'CLS',
  ];

  plan.blocks.forEach((block) => {
    const font = block.font;
    block.lines.forEach((line) => {
      const along = plan.margin + Math.max(0, Math.floor((plan.innerWidth - line.text.length * font.charWidth) / 2));
      const text = `"${font.id}",${rotated ? 90 : 0},${font.mul},${font.mul},"${escapeTspl(line.text)}"`;
      const origin = (shift) => (rotated
        ? `${plan.height - line.y},${along + shift}`
        : `${along + shift},${line.y}`);
      out.push(`TEXT ${origin(0)},${text}`);
      if (block.kind === 'name') out.push(`TEXT ${origin(font.mul)},${text}`);
    });
  });
  out.push(`PRINT 1,${Math.max(1, Math.round(input.copies || 1))}`);

  return out.join('\r\n') + '\r\n';
};

/**
 * Wrapped lines for an ESC/POS rendering (font A = 12 dots/char at 203 dpi; the
 * name is printed double width + height).
 *
 * @param {object} input {widthMm, heightMm, date, name, text}
 * @returns {{charsPerLine: number, date: string, nameLines: string[], textLines: string[], feedMm: number}}
 */
const planEscposLabel = (input = {}) => {
  const widthMm = toPositiveNumber(input.widthMm) || DEFAULT_LABEL_SIZE.widthMm;
  const heightMm = toPositiveNumber(input.heightMm) || DEFAULT_LABEL_SIZE.heightMm;
  const charsPerLine = Math.max(8, Math.floor(((widthMm - 4) * 8) / 12));
  const date = input.date || formatLabelDateTime();
  const nameLines = truncateLines(wrapLabelText(input.name || '', Math.floor(charsPerLine / 2)), 3);
  const textLines = wrapLabelText(input.text === undefined ? DEFAULT_LABEL_TEXT : input.text, charsPerLine);
  // ~3.75 mm per normal line, 7.5 mm per double-height line.
  const printedMm = 3.75 * (1 + textLines.length) + 7.5 * nameLines.length;

  return {charsPerLine, date, nameLines, textLines, feedMm: Math.max(0, Math.round(heightMm - printedMm))};
};

/**
 * Builds the printer program for a raw (TCP/USB) label job.
 *
 * @param {object} input {language, widthMm, heightMm, dpi, gapMm, date, name, text, copies}
 * @returns {{program: string, encoding: 'utf8'|'latin1'}|null} null for `escpos` (app-rendered)
 */
const buildLabelCommands = (input = {}) => {
  const language = normalizeLabelLanguage(input.language);
  if (language === 'zpl') return {program: buildZplLabel(input), encoding: 'utf8'};
  if (language === 'tspl') return {program: buildTsplLabel(input), encoding: 'latin1'};

  return null;
};

/**
 * Encodes a label program to bytes without relying on Buffer/TextEncoder.
 *
 * @param {string} program
 * @param {'utf8'|'latin1'} encoding
 * @returns {Uint8Array}
 */
const encodeLabelBytes = (program = '', encoding = 'utf8') => {
  const bytes = [];
  Array.from(String(program)).forEach((char) => {
    const code = char.codePointAt(0);
    if (encoding === 'latin1') {
      bytes.push(code <= 0xff ? code : 0x3f);
    } else if (code < 0x80) {
      bytes.push(code);
    } else if (code < 0x800) {
      bytes.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code < 0x10000) {
      bytes.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    } else {
      bytes.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  });

  return Uint8Array.from(bytes);
};

export {
  DEFAULT_LABEL_TEXT,
  DEFAULT_LABEL_SIZE,
  DEFAULT_LABEL_DPI,
  DEFAULT_LABEL_GAP_MM,
  LABEL_LANGUAGES,
  LABEL_SIZE_PRESETS,
  LABEL_ROTATIONS,
  MAX_PRINT_HEAD_MM,
  normalizeLabelRotation,
  formatLabelDateTime,
  resolveLabelName,
  resolveLabelCopies,
  normalizeLabelLanguage,
  resolveLabelSettings,
  wrapLabelText,
  planLabelLayout,
  buildZplLabel,
  buildTsplLabel,
  planEscposLabel,
  buildLabelCommands,
  encodeLabelBytes,
};
